import { FormControl, IconButton, Tooltip } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { useQuery } from '@tanstack/react-query'
import { inject, observer } from 'mobx-react'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { queryClient } from '../../../../../App'
import PrimaryCircularProgress from '../../../../../components/ProgressBackdrop/compoenents/PrimaryCircularProgress'
import DataUnit from '../../../../../components/customElements/DataUnit/DataUnit'
import FormInput from '../../../../../components/customElements/FormInput'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import {
  isAdministration,
  isOperator,
} from '../../../../../services/AccountService'
import { getIncedentTypes } from '../../../../../services/NewApiService'

const FormsOnTop = ({
  serviceStore,
  subscriberReason,
  incedentTypeReasons,
  isIncedentsLoading,
}) => {
  const intl = useIntl()
  const {
    reasonCategories,
    searchByIdMode,
    showAddServiceRequestForm,
    searchByPhoneMode,
    serviceAddRequestFormStore,
    showContinueInfo,
    checkedSeviceOrder,
    equipmentIdError,
    getInstalledOrders,
    showRequestPreview,
    serviceAddRequestFormStore: { data: formData },
  } = serviceStore

  const { data: incedentTypes, isLoading } = useQuery(
    {
      queryKey: ['incedent-types'],
      queryFn: () => getIncedentTypes(),
      enabled: () => isOperator(),
      staleTime: Infinity,
      placeholderData: [],
    },
    queryClient,
  )

  useEffect(() => {
    if (
      subscriberReason?.requiresExplanation &&
      !formData.requireReasonDiscription
    ) {
      serviceStore.serviceAddRequestFormStore.data.requireReasonDiscription = true
    } else if (
      !subscriberReason?.requiresExplanation &&
      formData.requireReasonDiscription
    ) {
      serviceStore.serviceAddRequestFormStore.data.requireReasonDiscription = false
      serviceStore.serviceAddRequestFormStore.data.malfunctionExplanation = ''
    }
  }, [subscriberReason])

  const validateFilterInputs = (event, propName) => {
    if (propName === 'phoneNumber') {
      const phonePattern = /^[0-9()-]+$/
      return phonePattern.test(event.target.value) || event.target.value === ''
        ? true
        : false
    }
    if (propName === 'equipmentId') {
      if (event) {
        if (event.target.value.length > 14) return false
        const idPattern = /^[0-9]+$/
        return idPattern.test(event.target.value) || event.target.value === ''
          ? true
          : false
      }
    }
    return true
  }

  const handleSearchByEnter = (e) => {
    if (e.key === 'Enter') {
      if (searchByIdMode && !equipmentIdError) {
        return getInstalledOrders()
      }

      if (searchByPhoneMode && !checkedSeviceOrder) {
        return getInstalledOrders()
      }
    }
  }

  return (
    <>
      {isLoading || isIncedentsLoading ? (
        <Stack
          justifyContent={'center'}
          alignItems={'center'}
          paddingTop={'50px'}
        >
          <PrimaryCircularProgress color='inherit' thickness={1} />
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            padding: '0 16px',
            marginTop: '24px',
          }}
          onKeyUp={handleSearchByEnter}
        >
          {!isOperator() && (
            <FormControl fullWidth>
              <FormInput
                api={serviceAddRequestFormStore}
                type='select'
                fullWidth
                autoSearch
                withoutErrorText
                propName='maintenanceReasonId'
                label={intl.formatMessage({ id: 'Причина обращения' })}
                items={reasonCategories.map((el) => {
                  return { key: el.name, value: el.id }
                })}
                disabled={true}
              />
            </FormControl>
          )}
          {searchByIdMode || showContinueInfo || showAddServiceRequestForm ? (
            <FormControl fullWidth>
              <FormInput
                api={serviceAddRequestFormStore}
                type={'text'}
                fullWidth
                autoSearch
                withoutErrorText
                propName='equipmentId'
                label={intl.formatMessage({ id: 'Id оборудования' })}
                renderCellValue={(params) => params.equipmentId}
                validateFilterInputs={validateFilterInputs}
                disabled={
                  showContinueInfo ||
                  showAddServiceRequestForm ||
                  isAdministration()
                }
              />
            </FormControl>
          ) : null}
          {searchByPhoneMode && (
            <FormControl fullWidth>
              <FormInput
                api={serviceAddRequestFormStore}
                type={'text'}
                fullWidth
                mask={'+7 (999)-999-99-99'}
                propName='phoneNumber'
                label={intl.formatMessage({ id: 'Номер телефона' })}
                renderCellValue={(params) => params.phone}
                withoutErrorText={true}
                ignoreErrorColor={true}
              />
            </FormControl>
          )}
          {isOperator() && !showRequestPreview && (
            <>
              <Tooltip
                placement='top'
                title={serviceAddRequestFormStore?.data?.incidentTypeId}
                disableHoverListener={
                  !serviceAddRequestFormStore?.data?.incidentTypeId
                }
              >
                <FormControl fullWidth>
                  <FormInput
                    api={serviceAddRequestFormStore}
                    type='select'
                    fullWidth
                    autoSearch
                    withoutErrorText
                    propName='incidentTypeId'
                    label={intl.formatMessage({ id: 'Тип инцидента' })}
                    items={incedentTypes.map((r) => r.name)}
                    ignoreErrorColor={!showAddServiceRequestForm}
                    showFullText
                  />
                </FormControl>
              </Tooltip>
              {showAddServiceRequestForm && (
                <>
                  <Tooltip
                    placement='top'
                    title={subscriberReason?.name}
                    disableHoverListener={!subscriberReason}
                  >
                    <FormControl fullWidth>
                      <FormInput
                        api={serviceAddRequestFormStore}
                        type='autocomplete'
                        fullWidth
                        autoSearch
                        withoutErrorText
                        propName='malfunctionTypeId'
                        label={intl.formatMessage({ id: 'Со слов абонента' })}
                        items={incedentTypeReasons.map((r) => r.name)}
                        error={!subscriberReason}
                        sx={{ input: { padding: '0 30px 0 4px !important' } }}
                      />
                      {!!formData.malfunctionTypeId && (
                        <IconButton
                          onClick={() => {
                            formData.malfunctionTypeId = ''
                          }}
                          sx={{
                            position: 'absolute',
                            top: '6px',
                            right: '40px',
                            padding: '2px',
                          }}
                        >
                          <img
                            src={getImageSrc('close')}
                            alt={'Стереть'}
                            style={{
                              width: '24px',
                            }}
                          />
                        </IconButton>
                      )}
                    </FormControl>
                  </Tooltip>
                  {subscriberReason?.requiresExplanation && (
                    <FormControl fullWidth>
                      <FormInput
                        api={serviceAddRequestFormStore}
                        type='text'
                        fullWidth
                        withoutErrorText
                        propName='malfunctionExplanation'
                        label={intl.formatMessage({ id: 'Описание' })}
                        multiline
                        paste={true}
                        error={!formData.malfunctionExplanation}
                      />
                    </FormControl>
                  )}
                </>
              )}
              <FormInput
                api={serviceAddRequestFormStore}
                type='checkbox'
                fullWidth
                propName='isUrgent'
                label={intl.formatMessage({ id: 'Срочная заявка' })}
                visible
                variant='text'
                icon={
                  <img
                    src={getImageSrc('fire')}
                    style={{ width: '18px', marginRight: '4px' }}
                  />
                }
              />
            </>
          )}

          {showRequestPreview && (
            <>
              <DataUnit
                name={'Id оборудования'}
                value={formData.equipmentId || '-'}
              />
              {isOperator() && (
                <>
                  <DataUnit
                    name={'Тип инцидента'}
                    value={formData.incidentTypeId || '-'}
                  />
                  <DataUnit
                    name={'Со слов абонента'}
                    value={formData.malfunctionTypeId}
                  />
                  {subscriberReason?.requiresExplanation && (
                    <DataUnit
                      name={'Описание'}
                      value={formData.malfunctionExplanation}
                    />
                  )}
                  <FormInput
                    api={serviceAddRequestFormStore}
                    type='checkbox'
                    fullWidth
                    propName='isUrgent'
                    label={intl.formatMessage({ id: 'Срочная заявка' })}
                    visible
                    variant='text'
                    readonly
                    icon={
                      <img
                        src={getImageSrc('fire')}
                        style={{ width: '18px', marginRight: '4px' }}
                      />
                    }
                  />
                </>
              )}
            </>
          )}
        </Box>
      )}
    </>
  )
}

export default inject('serviceStore')(observer(FormsOnTop))
